import React, { useEffect, useState } from 'react';
import { ArrowRightLeft, FileText, ImageOff, ListChecks, Loader2, MapPin, Play, StickyNote, UserRound } from 'lucide-react';
import api from '../../services/api';

// Tipos e componentes compartilhados entre o diálogo da lista de contatos e o histórico inline do atendimento.

export interface ZproTicket {
  id: number;
  protocolo?: string | null;
  status: string;
  canal?: string | null;
  grupo?: boolean;
  ultimaMensagem?: string | null;
  atendente?: string | null;
  fila?: string | null;
  totalMensagens: number;
  criadoEm: string;
  fechadoEm?: string | null;
}

export type ZproCategoria =
  | 'texto' | 'imagem' | 'audio' | 'video' | 'documento' | 'sticker' | 'contato'
  | 'localizacao' | 'interativo' | 'sistema' | 'nota' | 'outro';

export interface ZproMensagem {
  id: string;
  categoria: ZproCategoria;
  tipo?: string | null;
  corpo?: string | null;
  deletada: boolean;
  enviadaPorNos: boolean;
  midiaUrl?: string | null;
  temMidia: boolean;
  arquivoNome?: string | null;
  atendente?: string | null;
  remetente?: string | null;
  reacao?: string | null;
  editada: boolean;
  citada?: { corpo?: string | null; categoria?: ZproCategoria | null; enviadaPorNos: boolean } | null;
  criadaEm: string;
}

export const ZPRO_PAGE = 100;

export const statusLabel: Record<string, string> = { open: 'Aberto', pending: 'Pendente', closed: 'Fechado' };

const rotuloCategoria: Record<string, string> = {
  imagem: 'Imagem', audio: 'Áudio', video: 'Vídeo', documento: 'Documento', sticker: 'Figurinha',
  contato: 'Contato', localizacao: 'Localização', interativo: 'Mensagem interativa', outro: 'Mensagem',
};

export const fmt = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-';
export const fmtHora = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';
export const fmtDia = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
export const chaveDia = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');

export const erroMsg = (e: any) => e?.response?.data?.message || 'Não foi possível consultar o histórico do Zpro';

// vCard (BEGIN:VCARD ... FN:Nome ... TEL:+55...) -> nome e telefones; texto simples vira só o nome
const lerContato = (corpo?: string | null) => {
  const txt = corpo || '';
  if (!/BEGIN:VCARD/i.test(txt)) return { nomes: txt ? [txt] : [], telefones: [] as string[] };
  const nomes = Array.from(txt.matchAll(/^FN:(.+)$/gim)).map((m) => m[1].trim());
  const telefones = Array.from(txt.matchAll(/^(?:item\d+\.)?TEL[^:]*:(.+)$/gim)).map((m) => m[1].trim());
  return { nomes, telefones };
};

const Aviso: React.FC<{ icon?: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <p className="flex items-center gap-1 text-xs italic opacity-70 mb-1">
    {icon}
    {children}
  </p>
);

// Mídia lida do servidor do Zpro (SFTP) vem por uma rota da API, que exige o token:
// baixa como blob e usa uma URL local. `ativo` = false adia o download até o clique.
const useMidiaProtegida = (url: string | null | undefined, ativo: boolean) => {
  const protegida = !!url && url.startsWith('/');
  const [src, setSrc] = useState<string | null>(null);
  const [erro, setErro] = useState(false);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (!protegida || !ativo || !url) return;
    let cancelado = false;
    let objUrl: string | null = null;
    setCarregando(true);
    setErro(false);
    api
      .get(url, { responseType: 'blob' })
      .then((r) => {
        if (cancelado) return;
        objUrl = URL.createObjectURL(r.data);
        setSrc(objUrl);
      })
      .catch(() => !cancelado && setErro(true))
      .finally(() => !cancelado && setCarregando(false));
    return () => {
      cancelado = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [url, ativo, protegida]);

  return { protegida, src: protegida ? src : url || null, erro, carregando };
};

const Midia: React.FC<{ m: ZproMensagem }> = ({ m }) => {
  const { categoria, midiaUrl, temMidia, arquivoNome } = m;
  // Imagens e figurinhas carregam direto; áudio, vídeo e documentos só ao clicar
  const automatica = categoria === 'imagem' || categoria === 'sticker';
  const [pedida, setPedida] = useState(false);
  const { protegida, src, erro, carregando } = useMidiaProtegida(midiaUrl, automatica || pedida);

  const ehDocumento = !automatica && categoria !== 'audio' && categoria !== 'video';
  const baixar = (href: string) => {
    const a = document.createElement('a');
    a.href = href;
    a.download = arquivoNome || 'arquivo';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Documento protegido: quando o blob chega, dispara o download com o nome original
  useEffect(() => {
    if (protegida && pedida && src && ehDocumento) baixar(src);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protegida, pedida, src, ehDocumento]);

  if (categoria === 'contato' || categoria === 'localizacao' || categoria === 'interativo' || categoria === 'texto') {
    return null;
  }

  if (!midiaUrl) {
    if (!temMidia) return categoria === 'outro' ? null : <Aviso>[{rotuloCategoria[categoria] || 'Mídia'}]</Aviso>;
    return (
      <Aviso icon={<ImageOff className="h-3 w-3" />}>
        {rotuloCategoria[categoria] || 'Mídia'}
        {arquivoNome ? ` · ${arquivoNome}` : ''} — arquivo indisponível (configure a pasta das mídias)
      </Aviso>
    );
  }

  if (protegida && erro) {
    return (
      <Aviso icon={<ImageOff className="h-3 w-3" />}>
        {rotuloCategoria[categoria] || 'Mídia'}
        {arquivoNome ? ` · ${arquivoNome}` : ''} — arquivo não encontrado no servidor do Zpro
      </Aviso>
    );
  }

  if (protegida && carregando) {
    return (
      <Aviso icon={<Loader2 className="h-3 w-3 animate-spin" />}>Carregando {(rotuloCategoria[categoria] || 'mídia').toLowerCase()}...</Aviso>
    );
  }

  // Áudio/vídeo protegidos: botão até o primeiro clique
  if (protegida && !src && (categoria === 'audio' || categoria === 'video')) {
    return (
      <button
        type="button"
        onClick={() => setPedida(true)}
        className="inline-flex items-center gap-1 text-xs underline mb-1"
      >
        <Play className="h-3 w-3" /> {categoria === 'audio' ? 'Ouvir áudio' : 'Ver vídeo'}
      </button>
    );
  }

  if (protegida && ehDocumento) {
    return (
      <button
        type="button"
        onClick={() => (src ? baixar(src) : setPedida(true))}
        className="inline-flex items-center gap-1 text-xs underline mb-1 break-all text-left"
      >
        <FileText className="h-3 w-3 shrink-0" /> {arquivoNome || 'Baixar arquivo'}
      </button>
    );
  }

  if (!src) return null;

  if (categoria === 'imagem')
    return (
      <a href={src} target="_blank" rel="noreferrer">
        <img src={src} alt="Imagem" loading="lazy" className="max-w-full rounded mb-1 max-h-64" />
      </a>
    );
  if (categoria === 'sticker')
    return <img src={src} alt="Figurinha" loading="lazy" className="h-28 w-28 object-contain mb-1" />;
  if (categoria === 'audio')
    return <audio controls autoPlay={protegida} preload="none" src={src} className="mb-1 max-w-full" />;
  if (categoria === 'video')
    return <video controls autoPlay={protegida} preload="none" src={src} className="mb-1 max-w-full max-h-64 rounded" />;

  return (
    <a href={src} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs underline mb-1 break-all">
      <FileText className="h-3 w-3 shrink-0" /> {arquivoNome || 'Abrir arquivo'}
    </a>
  );
};

const Corpo: React.FC<{ m: ZproMensagem }> = ({ m }) => {
  if (m.deletada) return <em className="opacity-60">Mensagem apagada</em>;

  if (m.categoria === 'contato') {
    const { nomes, telefones } = lerContato(m.corpo);
    return (
      <div className="flex items-start gap-2">
        <UserRound className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p className="text-xs opacity-70">Contato compartilhado</p>
          {nomes.map((n, i) => (
            <p key={i} className="font-medium">{n}</p>
          ))}
          {telefones.map((t, i) => (
            <p key={i} className="text-xs">{t}</p>
          ))}
        </div>
      </div>
    );
  }

  if (m.categoria === 'localizacao') {
    return (
      <div className="flex items-start gap-2">
        <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p className="text-xs opacity-70">Localização</p>
          {m.corpo && <p className="break-all">{m.corpo}</p>}
        </div>
      </div>
    );
  }

  if (m.categoria === 'interativo') {
    return (
      <div>
        <Aviso icon={<ListChecks className="h-3 w-3" />}>Mensagem interativa (botões/lista)</Aviso>
        {m.corpo}
      </div>
    );
  }

  // O corpo de arquivos costuma repetir o nome; só mostra quando é uma legenda de verdade
  if (m.categoria !== 'texto' && m.corpo && m.arquivoNome && m.corpo.trim() === m.arquivoNome) return null;

  if (!m.corpo && m.categoria !== 'texto') return null;
  if (!m.corpo) return <em className="opacity-60">[{m.tipo || 'mensagem'} sem texto]</em>;
  return <>{m.corpo}</>;
};

export const ZproSeparadorDia: React.FC<{ iso: string }> = ({ iso }) => (
  <div className="flex justify-center py-1">
    <span className="text-[11px] rounded-full px-3 py-0.5 bg-gray-200 text-gray-600 dark:bg-slate-700 dark:text-gray-300">
      {fmtDia(iso)}
    </span>
  </div>
);

/** Uma mensagem do Zpro: chip central (transferência/nota interna) ou balão com mídia, citação e reação. */
export const ZproMensagemBolha: React.FC<{ m: ZproMensagem }> = ({ m }) => {
  if (m.categoria === 'sistema' || m.categoria === 'nota') {
    return (
      <div className="flex justify-center">
        <span
          className={`inline-flex items-center gap-1 text-xs rounded-md px-3 py-1 max-w-[90%] text-center ${
            m.categoria === 'nota'
              ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
              : 'bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100'
          }`}
        >
          {m.categoria === 'nota' ? <StickyNote className="h-3 w-3 shrink-0" /> : <ArrowRightLeft className="h-3 w-3 shrink-0" />}
          {m.corpo || (m.categoria === 'nota' ? 'Nota interna' : 'Atendimento transferido')} · {fmtHora(m.criadaEm)}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex ${m.enviadaPorNos ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
          m.enviadaPorNos
            ? 'bg-emerald-100 text-emerald-950 dark:bg-emerald-900/50 dark:text-emerald-50'
            : 'bg-gray-100 text-gray-900 dark:bg-slate-700 dark:text-gray-100'
        }`}
      >
        {!m.enviadaPorNos && m.remetente && <div className="text-xs font-semibold opacity-80 mb-0.5">{m.remetente}</div>}

        {m.citada && (
          <div className="mb-1 rounded border-l-4 border-emerald-500/70 bg-black/5 dark:bg-white/10 px-2 py-1 text-xs">
            <span className="font-semibold">{m.citada.enviadaPorNos ? 'Você' : 'Contato'}</span>
            <p className="line-clamp-2 opacity-80">
              {m.citada.corpo ||
                (m.citada.categoria && rotuloCategoria[m.citada.categoria] ? `[${rotuloCategoria[m.citada.categoria]}]` : 'Mensagem')}
            </p>
          </div>
        )}

        <Midia m={m} />
        <Corpo m={m} />

        {m.reacao && (
          <span className="inline-block mt-1 rounded-full bg-white/70 dark:bg-slate-900/60 px-1.5 text-sm leading-5 shadow-sm">
            {m.reacao}
          </span>
        )}

        <div className="text-[10px] opacity-60 mt-1 text-right">
          {m.enviadaPorNos && m.atendente ? `${m.atendente} · ` : ''}
          {m.editada ? 'editada · ' : ''}
          {fmtHora(m.criadaEm)}
        </div>
      </div>
    </div>
  );
};
